import React, { useState, memo, useEffect } from "react";
import axios from "axios";
import { Helmet } from 'react-helmet-async';
import SEO from "../components/common/SEO";
import emailjs from "@emailjs/browser";
import { useTranslation } from "react-i18next";
import CONFIG from "../config";
import { useBranding } from "../context/BrandingContext";
import BRANDING_FALLBACK from "../branding";

const Contact = memo(() => {
  const { branding, variables } = useBranding();
  const BRANDING = branding || BRANDING_FALLBACK;
  const { t, i18n } = useTranslation();

  const EMAILJS_SERVICE = variables?.EMAILJS_SERVICE_ID || "service_2ujhdcm";
  const EMAILJS_ADMIN_TEMPLATE = variables?.EMAILJS_TEMPLATE_ID_ADMIN || "ttemplate_a4suaqw";
  const EMAILJS_AUTOREPLY_TEMPLATE = variables?.EMAILJS_TEMPLATE_ID_AUTOREPLY || "template_58p3f7y";
  const EMAILJS_PUBLIC_KEY = variables?.EMAILJS_PUBLIC_KEY || "YF11AhE_Qq7iVhd0p";

  const isArabic = i18n.language?.startsWith('ar');
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    country: "",
    subject: "",
    message: "",
    botField: "",
    marketingConsent: false
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState("");
  const [error, setError] = useState("");
  const [infoList, setInfoList] = useState([]);
  const [openPhoneIndex, setOpenPhoneIndex] = useState(null);

  useEffect(() => {
    axios.get(`${CONFIG.API_URL}/api/contact/info`)
      .then(res => {
        const data = Array.isArray(res.data) ? res.data : (res.data ? [res.data] : []);
        setInfoList(data);

        // Initialize Tracking Pixels from all active records
        data.forEach(info => {
          if (info.facebook_pixel_id && window.fbq) {
            window.fbq('init', info.facebook_pixel_id);
          }
          if (info.tiktok_pixel_id && window.ttq) {
            window.ttq.load(info.tiktok_pixel_id);
          }
        });
      })
      .catch(err => console.error("Fetch info failed", err));
  }, []);

  // Aggregate Info
  const emails = Array.from(new Set([BRANDING.contact?.email, ...infoList.map(i => i.email)].filter(Boolean)));
  const phones = Array.from(new Set([BRANDING.contact?.phone1, BRANDING.contact?.phone2, ...infoList.flatMap(i => [i.phone1, i.phone2])].filter(Boolean)));
  const whatsappNumbers = Array.from(new Set([BRANDING.socialLinks?.whatsapp, ...infoList.map(i => i.whatsapp)].filter(Boolean)));

  const socialLinks = {
    facebook: Array.from(new Set([BRANDING.socialLinks?.facebook, ...infoList.map(i => i.facebook)].filter(Boolean))),
    instagram: Array.from(new Set([BRANDING.socialLinks?.instagram, ...infoList.map(i => i.instagram)].filter(Boolean))),
    tiktok: Array.from(new Set([BRANDING.socialLinks?.tiktok, ...infoList.map(i => i.tiktok)].filter(Boolean)))
  };

  const primaryInfo = infoList[0] || {};

  const validatePhone = (phone) => {
    if (!phone) return false;
    const digits = phone.replace(/\D/g, '');
    return digits.length >= 7 && digits.length <= 15;
  };

  const formatPhoneNumber = (value) => {
    if (!value) return value;
    const digits = value.replace(/\D/g, '');
    if (digits.length <= 3) return digits;
    if (digits.length <= 7) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
  };

  const validateForm = () => {
    const newErrors = {};

    if (!form.name.trim()) {
      newErrors.name = t('contact.errors.name_required', 'Name is required');
    } else if (form.name.trim().length < 2) {
      newErrors.name = t('contact.errors.name_too_short', 'Name must be at least 2 characters');
    }

    if (!form.email.trim()) {
      newErrors.email = t('contact.errors.email_required', 'Email is required');
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      newErrors.email = t('contact.errors.email_invalid', 'Please enter a valid email address');
    }

    if (!form.phone.trim()) {
      newErrors.phone = t('contact.errors.phone_required', 'Phone number is required');
    } else if (!validatePhone(form.phone)) {
      newErrors.phone = t('contact.errors.phone_invalid', 'Please enter a valid phone number');
    }

    if (!form.country) {
      newErrors.country = t('contact.errors.country_required', 'Country is required');
    }

    if (!form.subject.trim()) {
      newErrors.subject = t('contact.errors.subject_required', 'Subject is required');
    } else if (form.subject.trim().length < 3) {
      newErrors.subject = t('contact.errors.subject_too_short', 'Subject must be at least 3 characters');
    }

    if (!form.message.trim()) {
      newErrors.message = t('contact.errors.message_required', 'Message is required');
    } else if (form.message.trim().length < 10) {
      newErrors.message = t('contact.errors.message_too_short', 'Message must be at least 10 characters');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm({ ...form, [name]: value });

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors({ ...errors, [name]: '' });
    }

    // Auto-format phone number
    if (name === 'phone') {
      setForm({ ...form, [name]: formatPhoneNumber(value) });
    }
  };

  const countryCodes = {
    "Afghanistan": "+93", "Albania": "+355", "Algeria": "+213", "Andorra": "+376", "Angola": "+244", "Argentina": "+54", "Armenia": "+374", "Australia": "+61", "Austria": "+43", "Azerbaijan": "+994",
    "Bahrain": "+973", "Bangladesh": "+880", "Belarus": "+375", "Belgium": "+32", "Bolivia": "+591", "Bosnia and Herzegovina": "+387", "Brazil": "+55", "Bulgaria": "+359", "Burkina Faso": "+226", "Burundi": "+257",
    "Cambodia": "+855", "Cameroon": "+237", "Canada": "+1", "Cape Verde": "+238", "Central African Republic": "+236", "Chad": "+235", "Chile": "+56", "China": "+86", "Colombia": "+57", "Comoros": "+269",
    "Costa Rica": "+506", "Croatia": "+385", "Cuba": "+53", "Cyprus": "+357", "Czech Republic": "+420", "Denmark": "+45", "Djibouti": "+253", "Dominican Republic": "+1", "Ecuador": "+593", "Egypt": "+20", "El Salvador": "+503", "Equatorial Guinea": "+240", "Eritrea": "+291", "Estonia": "+372", "Ethiopia": "+251", "Fiji": "+679", "Finland": "+358", "France": "+33",
    "Gabon": "+241", "Gambia": "+220", "Georgia": "+995", "Germany": "+49", "Ghana": "+233", "Greece": "+30", "Guatemala": "+502", "Guinea": "+224", "Guyana": "+592", "Haiti": "+509", "Honduras": "+504", "Hungary": "+36",
    "Iceland": "+354", "India": "+91", "Indonesia": "+62", "Iran": "+98", "Iraq": "+964", "Ireland": "+353", "Israel": "+972", "Italy": "+39",
    "Jamaica": "+1", "Japan": "+81", "Jordan": "+962",
    "Kazakhstan": "+7", "Kenya": "+254", "Kuwait": "+965", "Kyrgyzstan": "+996",
    "Laos": "+856", "Latvia": "+371", "Lebanon": "+961", "Lesotho": "+266", "Liberia": "+231", "Libya": "+218", "Liechtenstein": "+423", "Lithuania": "+370", "Luxembourg": "+352",
    "Macedonia": "+389", "Madagascar": "+261", "Malawi": "+265", "Malaysia": "+60", "Maldives": "+960", "Mali": "+223", "Malta": "+356", "Mauritania": "+222", "Mauritius": "+230", "Mexico": "+52", "Moldova": "+373", "Monaco": "+377", "Mongolia": "+976", "Montenegro": "+382", "Morocco": "+212", "Mozambique": "+258", "Myanmar": "+95",
    "Namibia": "+264", "Nepal": "+977", "Netherlands": "+31", "New Zealand": "+64", "Nicaragua": "+505", "Niger": "+227", "Nigeria": "+234", "Norway": "+47",
    "Oman": "+968",
    "Pakistan": "+92", "Panama": "+507", "Papua New Guinea": "+675", "Paraguay": "+595", "Peru": "+51", "Philippines": "+63", "Poland": "+48", "Portugal": "+351", "Qatar": "+974",
    "Romania": "+40", "Russia": "+7", "Rwanda": "+250",
    "Saudi Arabia": "+966", "Senegal": "+221", "Serbia": "+381", "Seychelles": "+248", "Sierra Leone": "+232", "Singapore": "+65", "Slovakia": "+421", "Slovenia": "+386", "Somalia": "+252", "South Africa": "+27", "South Korea": "+82", "Spain": "+34", "Sri Lanka": "+94", "Sudan": "+249", "Sweden": "+46", "Switzerland": "+41", "Syria": "+963",
    "Taiwan": "+886", "Tajikistan": "+992", "Tanzania": "+255", "Thailand": "+66", "Togo": "+228", "Tunisia": "+216", "Turkey": "+90", "Turkmenistan": "+993", "Uganda": "+256", "Ukraine": "+380", "United Arab Emirates": "+971", "United Kingdom": "+44", "United States": "+1", "Uruguay": "+598", "Uzbekistan": "+998",
    "Venezuela": "+58", "Vietnam": "+84",
    "Yemen": "+967",
    "Zambia": "+260", "Zimbabwe": "+263"
  };

  async function sendEmailJS(data) {
    try {
      await Promise.all([
        emailjs.send(EMAILJS_SERVICE, EMAILJS_ADMIN_TEMPLATE, { ...data, time: new Date().toLocaleString() }, EMAILJS_PUBLIC_KEY),
        emailjs.send(EMAILJS_SERVICE, EMAILJS_AUTOREPLY_TEMPLATE, { name: data.name, title: data.subject, to_email: data.email, email: data.email }, EMAILJS_PUBLIC_KEY)
      ]);
    } catch (err) {
      console.error("EmailJS Error:", err);
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) {
      return;
    }
    setLoading(true);
    setSuccess("");
    setError("");
    try {
      const dialCode = countryCodes[form.country] || "";
      const finalPhone = form.phone.trim() ? `${dialCode} ${form.phone}`.trim() : "";
      const submissionData = { ...form, phone: finalPhone };

      await axios.post(`${CONFIG.API_URL}/api/contact`, submissionData);
      await sendEmailJS(submissionData);

      // Multi-Pixel Tracking
      infoList.forEach(info => {
        if (info.facebook_pixel_id && window.fbq) {
          window.fbq('trackSingle', info.facebook_pixel_id, 'Lead', { content_name: 'Contact' });
        }
      });
      if (window.ttq) { window.ttq.track('Contact'); }
      if (window.gtag) { window.gtag('event', 'generate_lead'); }

      setSuccess(t('contact.success'));
      setForm({ name: "", email: "", phone: "", country: "", subject: "", message: "", botField: "", marketingConsent: false });
    } catch (err) {
      setError(err.response?.data?.message || "Error sending message");
    } finally {
      setLoading(false);
    }
  };

  const renderFormField = (name, label, type = 'text', placeholder = '', required = true) => (
    <div className="group relative">
      <label className={`text-[10px] font-black uppercase tracking-widest mb-2 block ml-1 transition-colors ${errors[name] ? 'text-red-500' : 'text-gray-400 group-focus-within:text-brand-primary'}`}>
        {label}
      </label>
      <input
        name={name}
        type={type}
        value={form[name]}
        onChange={handleChange}
        className={`w-full p-5 bg-white/50 border rounded-3xl font-bold transition-all focus:ring-4 focus:outline-none ${errors[name]
          ? 'border-red-300 focus:ring-red-200 focus:border-red-500'
          : 'border-gray-100 focus:ring-brand-primary/10 focus:border-brand-primary hover:border-brand-primary/30'
          }`}
        placeholder={placeholder}
        required={required}
        aria-invalid={!!errors[name]}
        aria-describedby={errors[name] ? `error-${name}` : undefined}
      />
      {errors[name] && (
        <p id={`error-${name}`} className="mt-2 text-xs font-bold text-red-500 flex items-center gap-1" role="alert">
          <i className="fas fa-exclamation-circle text-[10px]"></i>
          {errors[name]}
        </p>
      )}
    </div>
  );

  return (
    <>
      <Helmet>
        <html lang={isArabic ? "ar" : "en"} dir={isArabic ? "rtl" : "ltr"} />
      </Helmet>
      <SEO title={isArabic ? "تواصل معنا" : "Contact Us"} />

      {/* Loading Overlay */}
      {loading && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/20 backdrop-blur-sm" role="alert" aria-live="polite">
          <div className="bg-white rounded-3xl p-8 shadow-2xl flex flex-col items-center gap-4">
            <div className="w-16 h-16 border-4 border-brand-primary/20 border-t-brand-primary rounded-full animate-spin"></div>
            <p className="text-sm font-black uppercase tracking-widest text-gray-600">{t('contact.sending', 'Sending your message...')}</p>
          </div>
        </div>
      )}

      <section className="max-w-7xl mx-auto py-24 px-6 lg:px-8 text-gray-800 pt-32 relative">
        {/* Background Accents */}
        <div className="absolute top-40 left-0 w-96 h-96 bg-brand-primary/5 rounded-full blur-3xl -z-10 animate-pulse"></div>
        <div className="absolute bottom-20 right-0 w-64 h-64 bg-brand-primary/10 rounded-full blur-3xl -z-10"></div>

        <div className="flex flex-col lg:flex-row gap-16 items-start">
          {/* Form Side */}
          <div className="flex-1 w-full translate-y-0 opacity-100 transition-all duration-1000">
            <div className="mb-12">
              <span className="text-xs font-black uppercase tracking-[0.2em] text-brand-primary mb-3 block animate-bounce-subtle">
                {t('contact.get_in_touch', 'Get in Touch')}
              </span>
              <h1 className="text-4xl lg:text-6xl font-black text-gray-900 tracking-tighter mb-6">
                {isArabic ? (primaryInfo.contact_title_ar || "تواصل معنا") : (primaryInfo.contact_title_en || "Contact Us")}
              </h1>
              <div className={`w-24 h-2 ${isArabic ? "bg-gradient-to-l" : "bg-gradient-to-r"} from-brand-primary to-brand-primary/40 rounded-full`} />
            </div>

            <form onSubmit={handleSubmit} className="space-y-8 p-1 sm:p-0" noValidate>
              <input
                name="botField"
                value={form.botField}
                onChange={handleChange}
                style={{ display: "none" }}
                aria-hidden="true"
              />

              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                {renderFormField('name', t('contact.name'), 'text', t('contact.placeholders.name', 'Your Full Name'))}
                {renderFormField('email', t('contact.email'), 'email', 'email@example.com')}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div className="group relative">
                  <label className={`text-[10px] font-black uppercase tracking-widest mb-2 block ml-1 ${errors.country ? 'text-red-500' : 'text-gray-400'}`}>
                    {t('contact.country')}
                  </label>
                  <select
                    name="country"
                    value={form.country}
                    onChange={handleChange}
                    className={`w-full p-5 bg-white/50 border rounded-3xl font-bold transition-all focus:ring-4 focus:outline-none appearance-none cursor-pointer ${errors.country
                      ? 'border-red-300 focus:ring-red-200 focus:border-red-500'
                      : 'border-gray-100 focus:ring-brand-primary/10 focus:border-brand-primary'
                      }`}
                    required
                    aria-invalid={!!errors.country}
                    aria-describedby={errors.country ? 'error-country' : undefined}
                  >
                    <option value="">{t('contact.select_country')}</option>
                    {Object.keys(countryCodes).map(c => (
                      <option key={c} value={c}>{c} ({countryCodes[c]})</option>
                    ))}
                  </select>
                  {errors.country && (
                    <p id="error-country" className="mt-2 text-xs font-bold text-red-500 flex items-center gap-1" role="alert">
                      <i className="fas fa-exclamation-circle text-[10px]"></i>
                      {errors.country}
                    </p>
                  )}
                </div>
                {renderFormField('phone', t('contact.phone'), 'tel', t('contact.placeholders.phone', 'Your Phone Number'))}
              </div>

              {renderFormField('subject', t('contact.subject'), 'text', '', true)}

              <div className="group relative">
                <label className={`text-[10px] font-black uppercase tracking-widest mb-2 block ml-1 ${errors.message ? 'text-red-500' : 'text-gray-400'}`}>
                  {t('contact.message')}
                </label>
                <textarea
                  name="message"
                  value={form.message}
                  onChange={handleChange}
                  className={`w-full p-5 bg-white/50 border rounded-3xl font-bold transition-all h-48 focus:ring-4 focus:outline-none resize-none ${errors.message
                    ? 'border-red-300 focus:ring-red-200 focus:border-red-500'
                    : 'border-gray-100 focus:ring-brand-primary/10 focus:border-brand-primary'
                    }`}
                  required
                  aria-invalid={!!errors.message}
                  aria-describedby={errors.message ? 'error-message' : undefined}
                />
                {errors.message && (
                  <p id="error-message" className="mt-2 text-xs font-bold text-red-500 flex items-center gap-1" role="alert">
                    <i className="fas fa-exclamation-circle text-[10px]"></i>
                    {errors.message}
                  </p>
                )}
              </div>

              {/* Law 151/2020: Direct Marketing Consent (Art. 17) */}
              <div className="flex items-start gap-4 p-6 bg-gray-50/50 rounded-[2rem] border border-gray-100 group hover:border-brand-primary/20 transition-all cursor-pointer" onClick={() => setForm(f => ({ ...f, marketingConsent: !f.marketingConsent }))}>
                <div className={`mt-1 w-6 h-6 rounded-lg border-2 flex items-center justify-center transition-all ${form.marketingConsent ? 'bg-brand-primary border-brand-primary text-white' : 'bg-white border-gray-200'}`}>
                  {form.marketingConsent && <i className="fas fa-check text-[10px]"></i>}
                </div>
                <div className="flex-1">
                  <p className="text-xs font-bold text-gray-700 leading-relaxed">
                    {t('contact.marketing_consent', 'I consent to receive direct marketing communications and updates from us via email or phone.')}
                  </p>
                  <p className="text-[10px] text-gray-400 mt-1">
                    {t('contact.marketing_consent_hint', 'You can withdraw your consent at any time.')}
                  </p>
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full py-6 bg-brand-primary text-white rounded-3xl font-black uppercase tracking-[0.2em] text-sm shadow-2xl shadow-brand-primary/30 hover:scale-[1.01] active:scale-95 transition-all flex items-center justify-center gap-3 disabled:opacity-70 disabled:cursor-not-allowed"
                aria-label={t('contact.send', 'Send message')}
              >
                {loading ? (
                  <>
                    <i className="fas fa-circle-notch fa-spin"></i>
                    {t('contact.send_loading')}
                  </>
                ) : (
                  <>
                    <i className="fas fa-paper-plane text-xs"></i>
                    {t('contact.send')}
                  </>
                )}
              </button>

              {success && (
                <div className="p-6 bg-green-500/10 text-green-600 rounded-3xl font-bold flex items-center gap-4 animate-slide-up border border-green-500/20" role="alert">
                  <i className="fas fa-check-circle text-xl"></i>
                  <div>
                    <p className="font-black">{t('contact.success')}</p>
                    <p className="text-sm mt-1">{t('contact.success_desc', 'Thank you! We will get back to you within 24 hours.')}</p>
                  </div>
                </div>
              )}
              {error && (
                <div className="p-6 bg-red-500/10 text-red-600 rounded-3xl font-bold flex items-center gap-4 animate-slide-up border border-red-500/20" role="alert">
                  <i className="fas fa-exclamation-circle text-xl"></i>
                  <div>
                    <p className="font-black">{t('contact.error', 'Oops! Something went wrong')}</p>
                    <p className="text-sm mt-1">{error}</p>
                  </div>
                </div>
              )}
            </form>
          </div>

          {/* Info Side */}
          <div className="lg:w-5/12 w-full space-y-10">
            {/* Glassmorphism Contact Card */}
            <div className="relative p-1 rounded-[3rem] bg-gradient-to-br from-white/20 to-transparent backdrop-blur-3xl border border-white/20 shadow-2xl overflow-hidden group">
              <div className="absolute inset-0 bg-white/5 -z-10"></div>

              <div className="p-10 space-y-10">
                <div className="flex items-center justify-between">
                  <h3 className="font-black text-3xl text-gray-900 tracking-tight">
                    {t('contact.other_ways', 'Direct Channels')}
                  </h3>
                  <div className="w-14 h-14 rounded-2xl bg-brand-primary/10 flex items-center justify-center text-brand-primary text-2xl group-hover:rotate-12 transition-transform">
                    <i className="fas fa-headset"></i>
                  </div>
                </div>

                {/* Emails */}
                <div className="space-y-4">
                  <span className="text-[10px] font-black uppercase tracking-[0.2em] text-brand-primary opacity-60 px-1">
                    {t('contact.email_label')}
                  </span>
                  <div className="flex flex-col gap-3">
                    {emails.map((email, idx) => (
                      <a
                        key={idx}
                        href={`mailto:${email}`}
                        className="group/link flex items-center gap-4 p-4 rounded-2xl hover:bg-white/40 transition-all border border-transparent hover:border-white/40 shadow-sm hover:shadow-md"
                        aria-label={`Email ${email}`}
                      >
                        <div className="w-10 h-10 rounded-xl bg-white flex items-center justify-center text-gray-400 group-hover/link:text-brand-primary transition-colors">
                          <i className="fas fa-envelope text-xs"></i>
                        </div>
                        <span className="font-bold text-gray-700 group-hover/link:text-brand-primary transition-colors">{email}</span>
                      </a>
                    ))}
                  </div>
                </div>

                {/* Phones */}
                <div className="space-y-4">
                  <span className="text-[10px] font-black uppercase tracking-[0.2em] text-brand-primary opacity-60 px-1">
                    {t('contact.phone_label')}
                  </span>
                  <div className="flex flex-col gap-3">
                    {phones.map((phone, idx) => (
                      <div key={idx} className="group/link relative">
                        <div
                          onClick={() => setOpenPhoneIndex(openPhoneIndex === idx ? null : idx)}
                          className="flex items-center justify-between p-4 rounded-2xl bg-white/20 hover:bg-white/40 transition-all border border-white/10 cursor-pointer"
                          aria-label={`Phone ${phone}`}
                        >
                          <div className="flex items-center gap-4">
                            <div className="w-10 h-10 rounded-xl bg-white flex items-center justify-center text-gray-400">
                              <i className="fas fa-phone-alt text-xs"></i>
                            </div>
                            <span className="font-black text-lg tracking-tight text-gray-800" dir="ltr">{phone}</span>
                          </div>
                          <i className={`fas fa-chevron-down text-[10px] transition-transform duration-300 ${openPhoneIndex === idx ? 'rotate-180' : ''}`}></i>
                        </div>

                        <div className={`overflow-hidden transition-all duration-300 ease-in-out ${openPhoneIndex === idx ? 'max-h-24 opacity-100 mt-2' : 'max-h-0 opacity-0'}`}>
                          <a
                            href={`tel:${phone}`}
                            className="flex items-center justify-center gap-2 py-4 bg-brand-primary text-white rounded-2xl font-black text-xs uppercase tracking-widest shadow-lg shadow-brand-primary/20"
                            aria-label={t('contact.call_now', 'Call Now')}
                          >
                            <i className="fas fa-phone-volume"></i>
                            {t('contact.call_now', 'Call Now')}
                          </a>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Socials */}
                <div className="pt-10 border-t border-gray-100">
                  <div className="flex flex-wrap gap-4">
                    {socialLinks.facebook.map((link, i) => (
                      <a key={`fb-${i}`} href={link} target="_blank" rel="noreferrer" className="w-12 h-12 rounded-2xl bg-white flex items-center justify-center text-gray-600 hover:bg-[#1877F2] hover:text-white transition-all hover:scale-110 shadow-sm border border-white/40" aria-label="Facebook">
                        <i className="fab fa-facebook-f"></i>
                      </a>
                    ))}
                    {socialLinks.instagram.map((link, i) => (
                      <a key={`ig-${i}`} href={link} target="_blank" rel="noreferrer" className="w-12 h-12 rounded-2xl bg-white flex items-center justify-center text-gray-600 hover:bg-gradient-to-tr from-[#f9ce34] via-[#ee2a7b] to-[#6228d7] hover:text-white transition-all hover:scale-110 shadow-sm border border-white/40" aria-label="Instagram">
                        <i className="fab fa-instagram"></i>
                      </a>
                    ))}
                    {socialLinks.tiktok.map((link, i) => (
                      <a key={`tk-${i}`} href={link} target="_blank" rel="noreferrer" className="w-12 h-12 rounded-2xl bg-white flex items-center justify-center text-gray-600 hover:bg-black hover:text-white transition-all hover:scale-110 shadow-sm border border-white/40" aria-label="TikTok">
                        <i className="fab fa-tiktok"></i>
                      </a>
                    ))}
                    {whatsappNumbers.map((num, i) => (
                      <a key={`wa-${i}`} href={`https://wa.me/${num.replace(/\D/g, '')}`} target="_blank" rel="noreferrer" className="w-12 h-12 rounded-2xl bg-white flex items-center justify-center text-gray-600 hover:bg-[#25D366] hover:text-white transition-all hover:scale-110 shadow-sm border border-white/40" aria-label="WhatsApp">
                        <i className="fab fa-whatsapp"></i>
                      </a>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Address & Hours cards */}
            <div className="space-y-6">
              {primaryInfo && (
                <div className="p-8 rounded-[2.5rem] bg-gray-50 border border-gray-100 group hover:border-brand-primary/20 transition-all">
                  <h4 className="font-black text-gray-900 mb-3 flex items-center gap-3">
                    <span className="w-1.5 h-6 bg-brand-primary rounded-full"></span>
                    {isArabic ? (primaryInfo.location_title_ar || t('contact.location_title', 'Location')) : (primaryInfo.location_title_en || t('contact.location_title', 'Location'))}
                  </h4>
                  <p className="font-bold text-gray-600 leading-relaxed mb-4">
                    {isArabic ? (primaryInfo.address_ar || t('contact.address', 'Address')) : (primaryInfo.address_en || t('contact.address', 'Address'))}
                  </p>

                  {/* Working Hours */}
                  <div className="space-y-3 mt-6 pt-6 border-t border-gray-200/60">
                    <div className="flex items-center gap-2 text-xs font-black uppercase tracking-widest text-brand-primary/60">
                      <i className="far fa-clock"></i>
                      {isArabic ? (primaryInfo.business_hours_title_ar || t('contact.businiss_hours_title', 'Businiss Hours')) : (primaryInfo.business_hours_title_en || t('contact.businiss_hours_title', 'Businiss Hours'))}
                    </div>
                    <p className="text-sm font-bold text-gray-600 leading-relaxed">
                      {isArabic ? (primaryInfo.working_hours_ar || t('contact.working_hours', 'Working Hours')) : (primaryInfo.working_hours_en || t('contact.working_hours', 'Working Hours'))}
                    </p>
                    {(isArabic ? (primaryInfo.business_hours_desc_en || t('contact.businiss_hours_desc', 'Businiss Hours Description')) : (primaryInfo.business_hours_desc_ar || t('contact.businiss_hours_desc', 'Businiss Hours Description'))) && (
                      <p className="text-xs font-medium text-gray-400 leading-relaxed">
                        {isArabic ? (primaryInfo.business_hours_desc_ar || t('contact.businiss_hours_desc', 'Businiss Hours Description')) : (primaryInfo.business_hours_desc_en || t('contact.businiss_hours_desc', 'Businiss Hours Description'))}
                      </p>
                    )}
                  </div>

                  {/* Rapid Response */}
                  {(primaryInfo.rapid_response_title_en || primaryInfo.rapid_response_title_ar || primaryInfo.rapid_response_desc_en || primaryInfo.rapid_response_desc_ar) && (
                    <div className="space-y-2 mt-6 pt-6 border-t border-gray-200/60">
                      <div className="flex items-center gap-2 text-xs font-black uppercase tracking-widest text-brand-primary/60">
                        <i className="fas fa-bolt"></i>
                        {isArabic ? (primaryInfo.rapid_response_title_ar || t('contact.rapid_response_title', 'Rapid Response')) : (primaryInfo.rapid_response_title_en || t('contact.rapid_response_title', 'Rapid Response'))}
                      </div>
                      <p className="text-sm font-bold text-gray-600 leading-relaxed">
                        {isArabic ? (primaryInfo.rapid_response_desc_ar || t('contact.rapid_response_desc', 'Rapid Response Description')) : (primaryInfo.rapid_response_desc_en || t('contact.rapid_response_desc', 'Rapid Response Description'))}
                      </p>
                    </div>
                  )}
                </div>
              )}

              {/* Default/Secondary info if no list */}
              {infoList.length === 0 && (
                <div className="p-8 rounded-[2.5rem] bg-gray-50 border border-gray-100">
                  <h4 className="font-black text-gray-900 mb-3">{t('contact.rapid_response')}</h4>
                  <p className="font-bold text-gray-600 leading-relaxed">
                    {t('contact.rapid_response_desc')}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      <style dangerouslySetInnerHTML={{
        __html: `
        @keyframes bounce-subtle {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-3px); }
        }
        .animate-bounce-subtle { animation: bounce-subtle 3s infinite ease-in-out; }
        .animate-slide-up { animation: slideUp 0.5s ease-out forwards; }
        @keyframes slideUp {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}} />
    </>
  );
});

export default Contact;
